// Planning du bureau (incrément 3) : la semaine, par personne ou par chantier. Lisible sur ordinateur (tableau jours × lignes),
// utilisable sur tablette (même tableau, colonne de gauche fixe) ; sur téléphone, liste jour par jour. Conflits (même personne
// sur deux chantiers le même jour) et postes en sous-effectif sont SIGNALÉS, jamais bloqués. Aucun montant.
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CalendarPlus, FlaskConical } from 'lucide-react';
import { api } from '../../../lib/api.ts';
import { pluriel } from '../../../lib/format.ts';
import { Alerte, Badge, Bouton, Chargement, EntetePage, EtatErreur, EtatVide, Onglets, Panneau } from '../../../components/ui.tsx';
import { OngletsInterventions } from './commun.tsx';
import { CRENEAUX, estWeekEnd, jourCourt } from './jours.ts';
import { DialogueAffecter, heuresTxt, NavigationSemaine } from './Planning.tsx';
import type { PlanningGlobal } from './types.ts';

type Vue = 'personnes' | 'chantiers';
type Aff = PlanningGlobal['affectations'][number];

export function PagePlanning() {
  const [params, setParams] = useSearchParams();
  const du = params.get('du');
  const vue: Vue = params.get('vue') === 'chantiers' ? 'chantiers' : 'personnes';
  const q = useQuery({ queryKey: ['planning', du ?? 'courante'], queryFn: () => api.get<PlanningGlobal>(`/api/planning${du ? `?du=${du}` : ''}`) });
  const [affecter, setAffecter] = useState<{ jours: string[]; personne?: string; chantier?: string } | null>(null);
  const changer = (cle: string, valeur: string | null) => {
    const n = new URLSearchParams(params);
    if (valeur) n.set(cle, valeur); else n.delete(cle);
    setParams(n, { replace: true });
  };
  const p = q.data;
  const index = useMemo(() => {
    const m = new Map<string, Aff[]>();
    for (const a of p?.affectations ?? []) {
      for (const cle of [`p|${a.personne_id}|${a.jour}`, `c|${a.chantier_id}|${a.jour}`]) m.set(cle, [...(m.get(cle) ?? []), a]);
    }
    return m;
  }, [p]);
  const chantierDe = (id: string) => p?.chantiers.find((c) => c.id === id);
  const nomDe = (id: string) => p?.personnes.find((x) => x.id === id)?.nom ?? 'Personne';
  const conflit = (personneId: string, jour: string) => p?.conflits.find((c) => c.personne_id === personneId && c.jour === jour);
  const sousEffectif = (chantierId: string, jour: string) => p?.sous_effectif.filter((s) => s.chantier_id === chantierId && s.jour === jour) ?? [];
  const joursDefaut = p ? (p.jours.includes(p.aujourdhui) ? [p.aujourdhui] : [p.jours[0]]) : [];
  const enCours = (p?.chantiers ?? []).filter((c) => c.status === 'lance');

  const Puce = ({ a, pour }: { a: Aff; pour: Vue }) => {
    const c = chantierDe(a.chantier_id);
    // Dans une case, le numéro sans son préfixe « CHA- » (2026-0001) tient sur une ligne, même sur tablette ; le numéro complet est en infobulle.
    const titre = pour === 'personnes' ? (c?.reference?.replace(/^CHA-/, '') ?? c?.title ?? 'Chantier') : nomDe(a.personne_id);
    const detail = [a.poste?.label ?? 'Tout le chantier', a.creneau !== 'journee' ? CRENEAUX[a.creneau] : null, heuresTxt(a.heures)].filter(Boolean).join(' — ');
    return (
      <Link to={`/interventions/chantiers/${a.chantier_id}?onglet=planning`} className={`pl-puce ${c?.demo ? 'pl-puce-demo' : ''}`}
        title={`${c?.reference ?? ''} ${c?.title ?? ''}${c?.demo ? ' (démonstration)' : ''} — ${nomDe(a.personne_id)} — ${detail}`}>
        <span className={pour === 'personnes' ? 'mono pl-puce-titre' : 'pl-puce-titre'}>{titre}</span>
        <span className="tres-petit secondaire pl-puce-detail">{detail}</span>
      </Link>
    );
  };

  const Cellule = ({ ligne, jour }: { ligne: string; jour: string }) => {
    const l = index.get(`${vue === 'personnes' ? 'p' : 'c'}|${ligne}|${jour}`) ?? [];
    const alerte = vue === 'personnes' ? conflit(ligne, jour)?.message : sousEffectif(ligne, jour).map((s) => s.message).join(' ; ') || undefined;
    return (
      <div className={`pl-cellule ${alerte ? 'pl-cellule-alerte' : ''}`}>
        {alerte && <span className="pl-cellule-signal" title={alerte}><AlertTriangle aria-hidden /><span className="visuellement-cache">{alerte}</span></span>}
        {l.map((a) => <Puce key={a.id} a={a} pour={vue} />)}
      </div>
    );
  };

  const lignes = vue === 'personnes'
    ? (p?.personnes ?? []).map((x) => ({ id: x.id, titre: x.nom, sous: x.actif ? null : 'Compte désactivé', demo: false }))
    : (p?.chantiers ?? []).map((c) => ({ id: c.id, titre: `${c.reference ?? ''} ${c.title}`.trim(), sous: [c.site, c.responsable ? `resp. ${c.responsable}` : null, c.status === 'suspendu' ? 'suspendu' : null].filter(Boolean).join(' — '), demo: c.demo }));

  return (
    <div className="pile-l">
      <EntetePage titre="Interventions" sousTitre="Planning : qui est où, jour par jour."
        actions={p && enCours.length > 0 && <Bouton variante="principal" icone={<CalendarPlus aria-hidden />} onClick={() => setAffecter({ jours: joursDefaut })}>Affecter</Bouton>} />
      <OngletsInterventions />
      {q.isLoading && <Chargement />}
      {q.isError && <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />}
      {p && (
        <>
          <div className="pl-barre">
            <NavigationSemaine du={p.du} aujourdhui={p.aujourdhui} changer={(x) => changer('du', x)} />
            <Onglets libelle="Afficher le planning" actif={vue} changer={(v) => changer('vue', v === 'personnes' ? null : v)}
              onglets={[{ id: 'personnes', libelle: 'Par personne' }, { id: 'chantiers', libelle: 'Par chantier' }]} />
          </div>
          {(p.conflits.length > 0 || p.sous_effectif.length > 0) && (
            <Alerte ton="ambre" titre={pluriel(p.conflits.length + p.sous_effectif.length, 'point à vérifier', 'points à vérifier')}>
              <ul className="pl-alertes">
                {p.conflits.map((c, i) => <li key={`c${i}`}>{nomDe(c.personne_id)} — {c.message}</li>)}
                {p.sous_effectif.map((s, i) => <li key={`s${i}`}>{chantierDe(s.chantier_id)?.reference ?? ''} {s.message}</li>)}
              </ul>
            </Alerte>
          )}
          {p.chantiers.length === 0 ? (
            <EtatVide titre="Aucun chantier en cours sur vos sites" texte="Un chantier se planifie une fois lancé (Interventions › Chantiers › le chantier › Lancer)." />
          ) : lignes.length === 0 ? (
            <EtatVide titre="Personne à afficher" texte="Aucun technicien n’a accès aux sites de ces chantiers ; l’administrateur ouvre les accès (Administration)." />
          ) : (
            <>
              {/* Ordinateur et tablette : tableau jours × lignes, colonne de gauche fixe. */}
              <Panneau serre className="pl-grille-panneau">
                <div className="tableau-conteneur pl-grille-conteneur">
                  <table className="tableau pl-grille">
                    <caption className="visuellement-cache">Planning de la semaine {vue === 'personnes' ? 'par personne' : 'par chantier'}</caption>
                    <thead>
                      <tr>
                        <th scope="col" className="pl-col-ligne">{vue === 'personnes' ? 'Personne' : 'Chantier'}</th>
                        {p.jours.map((j) => <th key={j} scope="col" className={`${j === p.aujourdhui ? 'pl-aujourdhui' : ''} ${estWeekEnd(j) ? 'pl-we' : ''}`}>{jourCourt(j)}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {lignes.map((l) => (
                        <tr key={l.id}>
                          <th scope="row" className="pl-col-ligne">
                            {vue === 'chantiers' ? <Link to={`/interventions/chantiers/${l.id}?onglet=planning`}>{l.titre}</Link> : <span>{l.titre}</span>}
                            {l.demo && <> <Badge ton="ambre" icone={<FlaskConical size={12} aria-hidden />}>Démo</Badge></>}
                            {l.sous && <span className="tres-petit discret pl-ligne-sous">{l.sous}</span>}
                          </th>
                          {p.jours.map((j) => <td key={j} className={`${j === p.aujourdhui ? 'pl-aujourdhui' : ''} ${estWeekEnd(j) ? 'pl-we' : ''}`}><Cellule ligne={l.id} jour={j} /></td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panneau>
              {p.chantiers.some((c) => c.demo) && <p className="tres-petit discret pl-legende">Bordure en tirets : chantier de démonstration (fictif).</p>}
              {/* Téléphone : jour par jour. */}
              <ol className="pl-jours pl-liste-telephone">
                {p.jours.map((j) => {
                  const l = p.affectations.filter((a) => a.jour === j);
                  return (
                    <li key={j} className={`pl-jour ${j === p.aujourdhui ? 'pl-aujourdhui' : ''}`}>
                      <div className="pl-jour-entete"><span className="pl-jour-nom">{jourCourt(j)}{j === p.aujourdhui && <Badge ton="accent" icone={false}>Aujourd’hui</Badge>}</span></div>
                      {l.length === 0 ? <p className="petit discret pl-vide">Aucune affectation</p> : (
                        <ul className="pl-affectations">
                          {l.map((a) => {
                            const c = chantierDe(a.chantier_id);
                            const alerte = conflit(a.personne_id, j);
                            return (
                              <li key={a.id} className="pl-affectation">
                                <Link to={`/interventions/chantiers/${a.chantier_id}?onglet=planning`} className="pl-affectation-texte pl-lien-bloc">
                                  <span className="pl-personne">{alerte && <AlertTriangle className="pl-alerte-icone" aria-label={alerte.message} />}{nomDe(a.personne_id)}</span>
                                  <span className="petit secondaire"><span className="mono">{c?.reference ?? ''}</span> {c?.title}{c?.demo ? ' (démo)' : ''}</span>
                                  <span className="tres-petit discret">{[a.poste?.label ?? 'Tout le chantier', a.creneau !== 'journee' ? CRENEAUX[a.creneau] : null, heuresTxt(a.heures)].filter(Boolean).join(' — ')}</span>
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ol>
            </>
          )}
          {affecter && <DialogueAffecter chantiers={enCours} semaine={p.jours} joursInitiaux={affecter.jours} personneInitiale={affecter.personne} fermer={() => setAffecter(null)} />}
        </>
      )}
    </div>
  );
}
